import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LocationProvider } from "./context/LocationContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { TenantProvider, useTenant } from "./context/TenantContext";
import { DomainMunicipioProvider } from "./contexts/DomainMunicipioContext";
import PWAInstallBanner from "./components/PWAInstallBanner";
import useSessionTracking from "./hooks/useSessionTracking";
import { getUserChosenMunicipio } from "./services/userChosenMunicipio";
import Header from "./components/Header";
import Footer from "./components/Footer";
import BottomNav from "./components/BottomNav";
import Home from "./pages/Home";
import Explorar from "./pages/Explorar";
import VitrineTematica from "./pages/VitrineTematica";
import Cadastrar from "./pages/Cadastrar";
import VerificarEmail from "./pages/VerificarEmail";
import CompletarPerfil from "./pages/CompletarPerfil";
import AceitarConvite from "./pages/AceitarConvite";
import Verificar2FA from "./pages/Verificar2FA";
import Perfil from "./pages/Perfil";
import CompleteSeuPerfil from "./pages/CompleteSeuPerfil";
import DistritoPage from "./pages/DistritoPage";
import NegocioDetalhes from "./pages/NegocioDetalhes";
import Sobre from "./pages/Sobre";
import Ajuda from "./pages/Ajuda";
import TermosDeUso from "./pages/TermosDeUso";
import PoliticaDePrivacidade from "./pages/PoliticaDePrivacidade";
import ContaPrivacidade from "./pages/ContaPrivacidade";
import Noticias from "./pages/Noticias";
import Favoritos from "./pages/Favoritos";
import ProgramasAcoesPage from "./pages/ProgramasAcoes";
import FaleConosco from "./pages/FaleConosco";
import Acessibilidade from "./pages/Acessibilidade";
import InstalarApp from "./pages/InstalarApp";
import Categorias from "./pages/Categorias";
import MunicipioLanding from "./pages/MunicipioLanding";
import MeusNegocios from "./pages/MeusNegocios";
import DeliveryKanban from "./pages/DeliveryKanban";
import DeliveryProdutos from "./pages/DeliveryProdutos";
import DeliveryCardapioPublico from "./pages/DeliveryCardapioPublico";
import DeliveryPedidoTrack from "./pages/DeliveryPedidoTrack";
import DeliveryDashboard from "./pages/DeliveryDashboard";
import DeliveryAssinatura from "./pages/DeliveryAssinatura";
import DeliveryCupons from "./pages/DeliveryCupons";
import MinhasReivindicacoes from "./pages/MinhasReivindicacoes";
import EsqueciSenha from "./pages/EsqueciSenha";
import RedefinirSenha from "./pages/RedefinirSenha";
import EditarNegocio from "./pages/EditarNegocio";
import MeusAlertas from "./pages/MeusAlertas";
import Vagas from "./pages/Vagas";
import Social from "./pages/Social";
import CadastroOrganizacao from "./pages/CadastroOrganizacao";
import OrganizacaoDetalhes from "./pages/OrganizacaoDetalhes";
import EditarOrganizacao from "./pages/EditarOrganizacao";
import FeedPublico from "./pages/FeedPublico";
import NovoPostFeed from "./pages/NovoPostFeed";
import MeusPostsFeed from "./pages/MeusPostsFeed";
import SolicitarAjuda from "./pages/SolicitarAjuda";
import MinhasSolicitacoes from "./pages/MinhasSolicitacoes";
const SolicitacoesAdminPage = lazy(() => import("./pages/admin/SolicitacoesAdminPage"));

// Admin imports
import { AdminProvider } from "./admin/context/AdminContext";
import { AdminLayoutWithProvider } from "./admin/components/AdminLayout";
import AdminProtectedRoute from "./admin/components/AdminProtectedRoute";
import AdminLogin from "./admin/pages/AdminLogin";
import Setup2FAObrigatorio from "./pages/admin/Setup2FAObrigatorio";
const VisaoGeral = lazy(() => import("./admin/pages/VisaoGeral"));
const AdminRelatorios = lazy(() => import("./admin/pages/AdminRelatorios"));
const Moderacao = lazy(() => import("./admin/pages/Moderacao"));
const NegociosLista = lazy(() => import("./admin/pages/NegociosLista"));
const NegocioEditorCompleto = lazy(() => import("./admin/pages/NegocioEditorCompleto"));
const NoticiasLista = lazy(() => import("./admin/pages/NoticiasLista"));
const NoticiaEditor = lazy(() => import("./admin/pages/NoticiaEditor"));
const PaginaLGPD = lazy(() => import("./admin/pages/PaginaLGPD"));
const PaginaTermos = lazy(() => import("./admin/pages/PaginaTermos"));
const PaginaCookies = lazy(() => import("./admin/pages/PaginaCookies"));
const UsuariosLista = lazy(() => import("./admin/pages/UsuariosLista"));
const GestoresAdmin = lazy(() => import("./admin/pages/GestoresAdmin"));
const AceitesPendentes = lazy(() => import("./admin/pages/AceitesPendentes"));
const MapaTerritorio = lazy(() => import("./admin/pages/MapaTerritorio"));
const Demanda = lazy(() => import("./admin/pages/Demanda"));
const Qualidade = lazy(() => import("./admin/pages/Qualidade"));
// Relatorios.js — página com 10 cards de relatórios temáticos
// Disponível em /admin/relatorios-detalhados (menu Gestão)
const Relatorios = lazy(() => import("./admin/pages/Relatorios"));
const Governanca = lazy(() => import("./admin/pages/Governanca"));
const ProgramasAcoes = lazy(() => import("./admin/pages/ProgramasAcoes"));
const DeliveryMunicipiosAdmin = lazy(() => import("./admin/pages/DeliveryMunicipiosAdmin"));
const ExportarBanco = lazy(() => import("./admin/pages/BackupAdmin"));
const PainelSocial = lazy(() => import("./pages/admin/PainelSocial"));
const OrganizacoesSociaisAdmin = lazy(() => import("./pages/admin/OrganizacoesSociaisAdmin"));
const VitrinesAdmin = lazy(() => import("./pages/admin/VitrinesAdmin"));
const PainelNegocios = lazy(() => import("./pages/admin/PainelNegocios"));
const PainelEmpreendedores = lazy(() => import("./pages/admin/PainelEmpreendedores"));
const Parceiros = lazy(() => import("./admin/pages/Parceiros"));
const Notificacoes = lazy(() => import("./admin/pages/Notificacoes"));
const NotificacoesEmail = lazy(() => import("./admin/pages/NotificacoesEmail"));
const Banners = lazy(() => import("./admin/pages/Banners"));
const Armazenamento = lazy(() => import("./admin/pages/Armazenamento"));
const SMSConfig = lazy(() => import("./admin/pages/SMSConfig"));
const CustosComunicacao = lazy(() => import("./admin/pages/CustosComunicacao"));
const DeliveryAdmin = lazy(() => import("./admin/pages/DeliveryAdmin"));
const DeliveryAdminPedidos = lazy(() => import("./admin/pages/DeliveryAdminPedidos"));
const DeliveryAssinaturasAdmin = lazy(() => import("./admin/pages/DeliveryAssinaturasAdmin"));
import NotFound from "./pages/NotFound";
import TagsIndex from "./pages/TagsIndex";
import TagPage from "./pages/TagPage";
const LimitesImagens = lazy(() => import("./admin/pages/LimitesImagens"));
const CategoriasAdmin = lazy(() => import("./admin/pages/CategoriasAdmin"));
const BackupAdmin = lazy(() => import("./admin/pages/BackupAdmin"));
const CuradoriaTags = lazy(() => import("./admin/pages/CuradoriaTags"));
const Denuncias = lazy(() => import("./admin/pages/Denuncias"));
const ConfiguracoesSite = lazy(() => import("./admin/pages/ConfiguracoesSite"));
const VerificacaoUsuariosAdmin = lazy(() => import("./admin/pages/VerificacaoUsuariosAdmin"));
const DiagnosticoVisibilidade = lazy(() => import("./admin/pages/DiagnosticoVisibilidade"));
const MunicipiosAdmin = lazy(() => import("./admin/pages/MunicipiosAdmin"));
const DisputasAdmin = lazy(() => import("./admin/pages/DisputasAdmin"));
const ModeracaoConteudo = lazy(() => import("./admin/pages/ModeracaoConteudo"));
const PalavrasProibidas = lazy(() => import("./admin/pages/PalavrasProibidas"));
const FaleConoscoAdmin = lazy(() => import("./admin/pages/FaleConoscoAdmin"));
const TerritoriosAdmin = lazy(() => import("./admin/pages/TerritoriosAdmin"));
const GeocodeConfigPage = lazy(() => import("./admin/pages/GeocodeConfigPage"));
const DashboardTerritorial = lazy(() => import("./admin/pages/DashboardTerritorial"));
const AlertasAdmin = lazy(() => import("./admin/pages/AlertasAdmin"));
const AlertasSistema = lazy(() => import("./admin/pages/AlertasSistema"));
const DefesaCivilAdmin = lazy(() => import("./admin/pages/DefesaCivilAdmin"));
const PWAStatsAdmin = lazy(() => import("./admin/pages/PWAStatsAdmin"));
const PermissoesAdmin = lazy(() => import("./admin/pages/PermissoesAdmin"));
const WhatsAppTemplate = lazy(() => import("./admin/pages/WhatsAppTemplate"));
const PainelSEO = lazy(() => import("./admin/pages/PainelSEO"));
const Integracoes = lazy(() => import("./admin/pages/Integracoes"));
const Seguranca2FA = lazy(() => import("./admin/pages/Seguranca2FA"));
import CategoriaCidade from "./pages/CategoriaCidade";
const FilaModeracaoPage = lazy(() => import("./pages/admin/FilaModeracaoPage"));
const ConfiguracoesIA = lazy(() => import("./pages/admin/ConfiguracoesIA"));
const DadosMock = lazy(() => import("./pages/admin/DadosMock"));
const EmailConfigPage = lazy(() => import("./pages/admin/EmailConfigPage"));
const EmailTemplatesPage = lazy(() => import("./pages/admin/EmailTemplatesPage"));
const EmailLogsPage = lazy(() => import("./pages/admin/EmailLogsPage"));
const CuponsAdminPage = lazy(() => import("./pages/admin/CuponsAdminPage"));
const FeatureFlagsPage = lazy(() => import("./pages/admin/FeatureFlagsPage"));
const ManutencaoAdmin = lazy(() => import("./pages/admin/ManutencaoAdmin"));
const GamificacaoAdminPage = lazy(() => import("./pages/admin/GamificacaoAdminPage"));
import CupomPage from "./pages/CupomPage";
import ValidarVoucherPage from "./pages/ValidarVoucherPage";

import CategoryOrBusinessRouter from "./components/CategoryOrBusinessRouter";
import CategoryRedirectWrapper from "./components/CategoryRedirectWrapper";

// DPO Panel pages
const DpoPainel = lazy(() => import("./admin/pages/dpo/DpoPainel"));
const DpoNomeacao = lazy(() => import("./admin/pages/dpo/DpoNomeacao"));
const DpoTitulares = lazy(() => import("./admin/pages/dpo/DpoTitulares"));
const DpoIncidentes = lazy(() => import("./admin/pages/dpo/DpoIncidentes"));
const DpoMapeamento = lazy(() => import("./admin/pages/dpo/DpoMapeamento"));
const DpoSubprocessadores = lazy(() => import("./admin/pages/dpo/DpoSubprocessadores"));
const DpoTreinamentos = lazy(() => import("./admin/pages/dpo/DpoTreinamentos"));
const DpoTemplates = lazy(() => import("./admin/pages/dpo/DpoTemplates"));
const DpoRelatorios = lazy(() => import("./admin/pages/dpo/DpoRelatorios"));
const DpoExport = lazy(() => import("./admin/pages/dpo/DpoExport"));
const AgendamentosRelatorios = lazy(() => import("./admin/pages/AgendamentosRelatorios"));
import PrivacidadeSolicitacao from "./pages/PrivacidadeSolicitacao";

// Recommendations admin
const Recomendacoes = lazy(() => import("./admin/pages/Recomendacoes"));

// Campanhas
const CampanhasLista = lazy(() => import("./admin/pages/CampanhasLista"));
const CampanhasForm = lazy(() => import("./admin/pages/CampanhasForm"));
const CampanhaRelatorio = lazy(() => import("./admin/pages/CampanhaRelatorio"));
const ColetivosLista = lazy(() => import("./admin/pages/ColetivosLista"));
const ColetivoForm = lazy(() => import("./admin/pages/ColetivoForm"));
const ColetivoInscritos = lazy(() => import("./admin/pages/ColetivoInscritos"));
const FAQAdmin = lazy(() => import("./admin/pages/FAQAdmin"));
const HeroBanners = lazy(() => import("./admin/pages/HeroBanners"));
const AjudaInterna = lazy(() => import("./admin/pages/AjudaInterna"));
const FAQPublica = lazy(() => import("./pages/FAQPublica"));
import ColetivosPublico from "./pages/ColetivosPublico";
import CadastrarColetivo from "./pages/CadastrarColetivo";
import ConviteColetivo from "./pages/ConviteColetivo";
import ColetivoPaginaPublica from "./pages/ColetivoPaginaPublica";
import ResponsavelColetivos, { ResponsavelColetivoGestao } from "./pages/ResponsavelColetivos";
import CampanhaPagina from "./pages/CampanhaPagina";
import CampanhasPublico from "./pages/CampanhasPublico";

// Main App Layout (for non-admin routes)
import { PublicAlertBar } from './components/PublicAlerts';
import DefesaCivilBanner from './components/DefesaCivilBanner';
import PendingAceitesGate from './components/PendingAceitesGate';
import LocationPrompt from './components/LocationPrompt';
import { useSessionHeartbeat } from './hooks/useSessionHeartbeat';
import CookieConsent from './components/CookieConsent';
import SiteFavicon from './components/SiteFavicon';
import PWASplashLinks from './components/PWASplashLinks';
import TrackingScripts from './components/TrackingScripts';
import EmailVerificationBanner from './components/EmailVerificationBanner';

function MainLayout({ children }) {
  useSessionHeartbeat();
  return (
    <div className="App">
      <a href="#main-content" className="skip-link" data-testid="skip-link">
        Pular para o conteúdo principal
      </a>
      <PendingAceitesGate />
      <DefesaCivilBanner />
      <PublicAlertBar />
      <LocationPrompt />
      <EmailVerificationBanner />
      <Header />
      <main id="main-content" role="main" tabIndex={-1}>
        {children}
      </main>
      <Footer />
      <div className="h-20" aria-hidden="true" /> {/* Spacer p/ BottomNav (todas dimensões) */}
      <BottomNav />
    </div>
  );
}

// Wrapper for tenant routes
function TenantRoutes() {
  return (
    <TenantProvider>
      <Routes>
        <Route index element={<MainLayout><Home /></MainLayout>} />
        <Route path="explorar" element={<MainLayout><Explorar /></MainLayout>} />
        <Route path="produtos-da-roca" element={<MainLayout><VitrineTematica tema="roca" /></MainLayout>} />
        <Route path="economia-criativa" element={<MainLayout><VitrineTematica tema="criativa" /></MainLayout>} />
        <Route path="cadastrar" element={<MainLayout><Cadastrar /></MainLayout>} />
        <Route path="perfil" element={<MainLayout><Perfil /></MainLayout>} />
        <Route path="complete-seu-perfil" element={<MainLayout><CompleteSeuPerfil /></MainLayout>} />
        <Route path="verificar-email" element={<VerificarEmail />} />
        <Route path="conta/privacidade" element={<MainLayout><ContaPrivacidade /></MainLayout>} />
        <Route path="negocio/:id" element={<MainLayout><NegocioDetalhes /></MainLayout>} />
        <Route path="sobre" element={<MainLayout><Sobre /></MainLayout>} />
        <Route path="ajuda" element={<MainLayout><Ajuda /></MainLayout>} />
        <Route path="termos-de-uso" element={<MainLayout><TermosDeUso /></MainLayout>} />
        <Route path="politica-de-privacidade" element={<MainLayout><PoliticaDePrivacidade /></MainLayout>} />
        <Route path="noticias" element={<MainLayout><Noticias /></MainLayout>} />
        <Route path="noticias/:slug" element={<MainLayout><Noticias /></MainLayout>} />
        <Route path="favoritos" element={<MainLayout><Favoritos /></MainLayout>} />
        <Route path="promocoes" element={<Navigate to="../campanhas" replace />} />
        <Route path="campanhas" element={<MainLayout><CampanhasPublico /></MainLayout>} />
        <Route path="campanhas/:id" element={<MainLayout><CampanhaPagina /></MainLayout>} />
        <Route path="coletivos" element={<MainLayout><ColetivosPublico /></MainLayout>} />
        <Route path="coletivos/cadastrar" element={<MainLayout><CadastrarColetivo /></MainLayout>} />
        <Route path="coletivos/convite/:token" element={<MainLayout><ConviteColetivo /></MainLayout>} />
        <Route path="coletivos/:slug" element={<MainLayout><ColetivoPaginaPublica /></MainLayout>} />
        <Route path="feed" element={<MainLayout><FeedPublico /></MainLayout>} />
        <Route path="empreendedor/feed/novo" element={<MainLayout><NovoPostFeed /></MainLayout>} />
        <Route path="empreendedor/feed/posts" element={<MainLayout><MeusPostsFeed /></MainLayout>} />
        <Route path="precisar-de" element={<MainLayout><SolicitarAjuda /></MainLayout>} />
        <Route path="minhas-solicitacoes" element={<MainLayout><MinhasSolicitacoes /></MainLayout>} />
        <Route path="cupom/:codigo" element={<MainLayout><CupomPage /></MainLayout>} />
        <Route path="validar-voucher/:code" element={<ValidarVoucherPage />} />
        <Route path="vagas" element={<MainLayout><Vagas /></MainLayout>} />
        <Route path="faq" element={<MainLayout><FAQPublica /></MainLayout>} />
        <Route path="privacidade/solicitacao" element={<MainLayout><PrivacidadeSolicitacao /></MainLayout>} />
        <Route path="social" element={<MainLayout><Social /></MainLayout>} />
        <Route path="social/:orgId" element={<MainLayout><OrganizacaoDetalhes /></MainLayout>} />
        <Route path="social/:municipioSlug/:orgSlug" element={<MainLayout><OrganizacaoDetalhes /></MainLayout>} />
        <Route path="social/:orgId/editar" element={<MainLayout><EditarOrganizacao /></MainLayout>} />
        <Route path="social/cadastrar-organizacao" element={<MainLayout><CadastroOrganizacao /></MainLayout>} />
        <Route path="cadastrar-organizacao" element={<Navigate to="../social/cadastrar-organizacao" replace />} />
        <Route path="programas" element={<MainLayout><ProgramasAcoesPage /></MainLayout>} />
        <Route path="fale-conosco" element={<MainLayout><FaleConosco /></MainLayout>} />
        <Route path="acessibilidade" element={<MainLayout><Acessibilidade /></MainLayout>} />
        <Route path="instalar" element={<MainLayout><InstalarApp /></MainLayout>} />
        <Route path="categorias" element={<MainLayout><Categorias /></MainLayout>} />
        <Route path="distrito/:distritoSlug" element={<MainLayout><DistritoPage /></MainLayout>} />
        <Route path="tags" element={<MainLayout><TagsIndex /></MainLayout>} />
        <Route path="tags/:tagSlug" element={<MainLayout><TagPage /></MainLayout>} />
        {/* SEO-friendly URL: /:municipioSlug/:categoriaSlug/:secondSlug
            The second segment can be either a subcategoria (renders Explorar
            with subcategoria filter) or a businessSlug (renders NegocioDetalhes).
            Resolved at runtime by CategoryOrBusinessRouter. */}
        <Route path=":categoriaSlug/:secondSlug" element={<MainLayout><CategoryOrBusinessRouter /></MainLayout>} />
        {/* SEO-friendly categoria listing: /:municipioSlug/:categoriaSlug (canonical) */}
        <Route path=":categoriaSlug" element={<MainLayout><CategoryRedirectWrapper /></MainLayout>} />
        {/* Catch-all 404 inside tenant scope */}
        <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
      </Routes>
    </TenantProvider>
  );
}

// Redirect /mg/:municipioSlug to /:municipioSlug
function MgRedirect() {
  const { municipioSlug } = useParams();
  return <Navigate to={`/${municipioSlug}`} replace />;
}

// Redirect /m/:municipioSlug to /:municipioSlug (legacy/typo guard)
function MShortRedirect() {
  const { municipioSlug } = useParams();
  return <Navigate to={`/${municipioSlug}`} replace />;
}

function SessionTracker() {
  // Hook needs auth + chosen municipio. Both are optional — anonymous
  // visitors are tracked via anonymous_id.
  const { token } = useAuth() || {};
  useSessionTracking({
    apiBase: process.env.REACT_APP_BACKEND_URL,
    getMunicipioId: () => {
      const m = getUserChosenMunicipio();
      return m && m.id ? m.id : null;
    },
    getAuthHeader: () => (token ? `Bearer ${token}` : null),
  });
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <DomainMunicipioProvider>
      <AuthProvider>
        <LocationProvider>
          <FavoritesProvider>
            <SessionTracker />
            <Routes>
              {/* Root - Shows municipality selection or redirects */}
              <Route path="/" element={<MainLayout><MunicipioLanding /></MainLayout>} />

              {/* SEO Routes - state/city/category/subcategory */}
              <Route path="/mg/:municipioSlug/:categoriaSlug/:subcategoriaSlug" element={<MainLayout><CategoriaCidade /></MainLayout>} />
              <Route path="/mg/:municipioSlug/:categoriaSlug" element={<MainLayout><CategoriaCidade /></MainLayout>} />
              <Route path="/mg/:municipioSlug" element={<MgRedirect />} />
              <Route path="/m/:municipioSlug" element={<MShortRedirect />} />
              
              {/* SEO Business route with slug */}
              <Route path="/negocio/:businessSlug" element={<MainLayout><NegocioDetalhes /></MainLayout>} />
              
              {/* Legacy routes (without municipality prefix) - still work for backwards compatibility */}
              <Route path="/explorar" element={<MainLayout><Explorar /></MainLayout>} />
              <Route path="/produtos-da-roca" element={<MainLayout><VitrineTematica tema="roca" /></MainLayout>} />
              <Route path="/economia-criativa" element={<MainLayout><VitrineTematica tema="criativa" /></MainLayout>} />
              <Route path="/cadastrar" element={<MainLayout><Cadastrar /></MainLayout>} />
              <Route path="/verificar-email" element={<VerificarEmail />} />
              <Route path="/completar-perfil" element={<CompletarPerfil />} />
              <Route path="/aceitar-convite/:token" element={<AceitarConvite />} />
              <Route path="/verificar-2fa" element={<Verificar2FA />} />
              <Route path="/perfil" element={<MainLayout><Perfil /></MainLayout>} />
              <Route path="/conta/privacidade" element={<MainLayout><ContaPrivacidade /></MainLayout>} />
              <Route path="/complete-seu-perfil" element={<MainLayout><CompleteSeuPerfil /></MainLayout>} />
              <Route path="/meus-negocios" element={<MainLayout><MeusNegocios /></MainLayout>} />
              <Route path="/meus-negocios/alertas" element={<MainLayout><MeusAlertas /></MainLayout>} />
              <Route path="/empreendedor/delivery/:negocioId" element={<MainLayout><DeliveryKanban /></MainLayout>} />
              <Route path="/empreendedor/delivery/:negocioId/produtos" element={<MainLayout><DeliveryProdutos /></MainLayout>} />
              <Route path="/empreendedor/delivery/:negocioId/dashboard" element={<MainLayout><DeliveryDashboard /></MainLayout>} />
              <Route path="/empreendedor/delivery/:negocioId/cupons" element={<MainLayout><DeliveryCupons /></MainLayout>} />
              <Route path="/empreendedor/delivery/:negocioId/assinatura" element={<MainLayout><DeliveryAssinatura /></MainLayout>} />
              <Route path="/delivery/p/:numero" element={<DeliveryPedidoTrack />} />
              <Route path="/delivery/:municipioSlug/:negocioSlug" element={<DeliveryCardapioPublico />} />
              <Route path="/editar-negocio/:id" element={<MainLayout><EditarNegocio /></MainLayout>} />
              <Route path="/minhas-reivindicacoes" element={<MainLayout><MinhasReivindicacoes /></MainLayout>} />
              <Route path="/esqueci-senha" element={<EsqueciSenha />} />
              <Route path="/redefinir-senha" element={<RedefinirSenha />} />
              <Route path="/negocio/:id" element={<MainLayout><NegocioDetalhes /></MainLayout>} />
              <Route path="/tags" element={<MainLayout><TagsIndex /></MainLayout>} />
              <Route path="/tags/:tagSlug" element={<MainLayout><TagPage /></MainLayout>} />
              <Route path="/sobre" element={<MainLayout><Sobre /></MainLayout>} />
              <Route path="/ajuda" element={<MainLayout><Ajuda /></MainLayout>} />
              <Route path="/termos-de-uso" element={<MainLayout><TermosDeUso /></MainLayout>} />
              <Route path="/politica-de-privacidade" element={<MainLayout><PoliticaDePrivacidade /></MainLayout>} />
              <Route path="/noticias" element={<MainLayout><Noticias /></MainLayout>} />
              <Route path="/noticias/:slug" element={<MainLayout><Noticias /></MainLayout>} />
              <Route path="/favoritos" element={<MainLayout><Favoritos /></MainLayout>} />
              <Route path="/promocoes" element={<Navigate to="/campanhas" replace />} />
              <Route path="/campanhas" element={<MainLayout><CampanhasPublico /></MainLayout>} />
              <Route path="/campanhas/:id" element={<MainLayout><CampanhaPagina /></MainLayout>} />
              <Route path="/coletivos" element={<MainLayout><ColetivosPublico /></MainLayout>} />
              <Route path="/coletivos/cadastrar" element={<MainLayout><CadastrarColetivo /></MainLayout>} />
              <Route path="/coletivos/convite/:token" element={<MainLayout><ConviteColetivo /></MainLayout>} />
              <Route path="/coletivos/:slug" element={<MainLayout><ColetivoPaginaPublica /></MainLayout>} />
              <Route path="/responsavel/coletivos" element={<MainLayout><ResponsavelColetivos /></MainLayout>} />
              <Route path="/responsavel/coletivos/:id" element={<MainLayout><ResponsavelColetivoGestao /></MainLayout>} />
              <Route path="/cupom/:codigo" element={<MainLayout><CupomPage /></MainLayout>} />
              <Route path="/validar-voucher/:code" element={<ValidarVoucherPage />} />
              <Route path="/vagas" element={<MainLayout><Vagas /></MainLayout>} />
              <Route path="/faq" element={<MainLayout><FAQPublica /></MainLayout>} />
              <Route path="/social" element={<MainLayout><Social /></MainLayout>} />
              <Route path="/social/:orgId" element={<MainLayout><OrganizacaoDetalhes /></MainLayout>} />
              <Route path="/social/:municipioSlug/:orgSlug" element={<MainLayout><OrganizacaoDetalhes /></MainLayout>} />
              <Route path="/social/:orgId/editar" element={<MainLayout><EditarOrganizacao /></MainLayout>} />
              <Route path="/social/cadastrar-organizacao" element={<MainLayout><CadastroOrganizacao /></MainLayout>} />
              <Route path="/:municipioSlug/distrito/:distritoSlug" element={<MainLayout><DistritoPage /></MainLayout>} />
              <Route path="/cadastrar-organizacao" element={<Navigate to="/social/cadastrar-organizacao" replace />} />
              <Route path="/programas" element={<MainLayout><ProgramasAcoesPage /></MainLayout>} />
              <Route path="/fale-conosco" element={<MainLayout><FaleConosco /></MainLayout>} />
              <Route path="/acessibilidade" element={<MainLayout><Acessibilidade /></MainLayout>} />
              <Route path="/instalar" element={<MainLayout><InstalarApp /></MainLayout>} />
              <Route path="/categorias" element={<MainLayout><Categorias /></MainLayout>} />
              <Route path="/feed" element={<MainLayout><FeedPublico /></MainLayout>} />
              <Route path="/empreendedor/feed/novo" element={<MainLayout><NovoPostFeed /></MainLayout>} />
              <Route path="/empreendedor/feed/posts" element={<MainLayout><MeusPostsFeed /></MainLayout>} />
              <Route path="/precisar-de" element={<MainLayout><SolicitarAjuda /></MainLayout>} />
              <Route path="/minhas-solicitacoes" element={<MainLayout><MinhasSolicitacoes /></MainLayout>} />

              {/* Admin Login Route - Unprotected */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/2fa-setup-obrigatorio" element={<Setup2FAObrigatorio />} />

              {/* Admin Routes - Protected (Global for ADMIN_MASTER) */}
              <Route path="/admin/*" element={
                <AdminProtectedRoute>
                  <AdminProvider>
                    <AdminLayoutWithProvider />
                  </AdminProvider>
                </AdminProtectedRoute>
              }>
                <Route index element={<VisaoGeral />} />
                <Route path="visao-geral" element={<VisaoGeral />} />
                <Route path="relatorios" element={<AdminRelatorios />} />
                <Route path="relatorios/agendamentos" element={<AgendamentosRelatorios />} />
                <Route path="mapa-territorio" element={<MapaTerritorio />} />
                <Route path="moderacao" element={<FilaModeracaoPage />} />
                <Route path="moderacao-conteudo" element={<ModeracaoConteudo />} />
                <Route path="palavras-proibidas" element={<PalavrasProibidas />} />
                <Route path="fale-conosco" element={<FaleConoscoAdmin />} />
                <Route path="configuracoes-ia" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <ConfiguracoesIA />
                  </AdminProtectedRoute>
                } />
                <Route path="dados-mock" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <DadosMock />
                  </AdminProtectedRoute>
                } />
                <Route path="moderacao/:id" element={<NegocioEditorCompleto />} />
                <Route path="denuncias" element={<Denuncias />} />
                <Route path="negocios" element={<NegociosLista />} />
                <Route path="negocios/novo" element={<NegocioEditorCompleto />} />
                <Route path="negocios/aceites-pendentes" element={<AceitesPendentes />} />
                <Route path="negocios/:id" element={<NegocioEditorCompleto />} />
                <Route path="negocios/:id/:action" element={<NegocioEditorCompleto />} />
                <Route path="demanda" element={<Demanda />} />
                <Route path="qualidade" element={<Qualidade />} />
                <Route path="conteudo/noticias" element={<NoticiasLista />} />
                <Route path="conteudo/noticias/nova" element={<NoticiaEditor />} />
                <Route path="conteudo/noticias/:id/editar" element={<NoticiaEditor />} />
                <Route path="conteudo/lgpd" element={<PaginaLGPD />} />
                <Route path="conteudo/termos-de-uso" element={<PaginaTermos />} />
                <Route path="conteudo/cookies" element={<PaginaCookies />} />
                <Route path="empreendedores" element={<UsuariosLista tipo="empreendedor" />} />
                <Route path="consumidores" element={<UsuariosLista tipo="consumidor" />} />
                <Route path="gestores" element={<GestoresAdmin />} />
                <Route path="usuarios" element={<UsuariosLista tipo="empreendedor" />} />
                <Route path="programas-acoes" element={<ProgramasAcoes />} />
                <Route path="relatorios-detalhados" element={<Relatorios />} />
                <Route path="governanca-lgpd" element={<Governanca />} />
                <Route path="municipios" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <MunicipiosAdmin />
                  </AdminProtectedRoute>
                } />
                <Route path="territorios" element={<TerritoriosAdmin />} />
                <Route path="geocode-config" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <GeocodeConfigPage />
                  </AdminProtectedRoute>
                } />
                <Route path="dashboard-territorial" element={<DashboardTerritorial />} />
                <Route path="disputas" element={<DisputasAdmin />} />
                <Route path="parceiros" element={<Parceiros />} />
                <Route path="alertas" element={<AlertasAdmin />} />
                <Route path="alertas-sistema" element={<AlertasSistema />} />
                <Route path="defesa-civil" element={<DefesaCivilAdmin />} />
                <Route path="pwa-stats" element={<PWAStatsAdmin />} />
                <Route path="permissoes" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <PermissoesAdmin />
                  </AdminProtectedRoute>
                } />
                <Route path="notificacoes" element={<Notificacoes />} />
                <Route path="emails" element={<NotificacoesEmail />} />
                <Route path="email-config" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <EmailConfigPage />
                  </AdminProtectedRoute>
                } />
                <Route path="email-templates" element={<EmailTemplatesPage />} />
                <Route path="email-logs" element={<EmailLogsPage />} />
                <Route path="cupons" element={<CuponsAdminPage />} />
                <Route path="feature-flags" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <FeatureFlagsPage />
                  </AdminProtectedRoute>
                } />
                <Route path="manutencao" element={<ManutencaoAdmin />} />
                <Route path="gamificacao" element={<GamificacaoAdminPage />} />
                <Route path="solicitacoes" element={<SolicitacoesAdminPage />} />
                <Route path="whatsapp-template" element={<WhatsAppTemplate />} />
                <Route path="banners" element={<Banners />} />
                <Route path="armazenamento" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <Armazenamento />
                  </AdminProtectedRoute>
                } />
                <Route path="sms" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <SMSConfig />
                  </AdminProtectedRoute>
                } />
                <Route path="custos-comunicacao" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <CustosComunicacao />
                  </AdminProtectedRoute>
                } />
                <Route path="delivery" element={<DeliveryAdmin />} />
                <Route path="delivery/pedidos" element={<DeliveryAdminPedidos />} />
                <Route path="delivery/assinaturas" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <DeliveryAssinaturasAdmin />
                  </AdminProtectedRoute>
                } />
                <Route path="delivery/municipios" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <DeliveryMunicipiosAdmin />
                  </AdminProtectedRoute>
                } />
                <Route path="exportar-banco" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <ExportarBanco />
                  </AdminProtectedRoute>
                } />
                <Route path="limites-imagens" element={<LimitesImagens />} />
                <Route path="categorias" element={<CategoriasAdmin />} />
                <Route path="backup" element={<BackupAdmin />} />
                <Route path="curadoria-tags" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <CuradoriaTags />
                  </AdminProtectedRoute>
                } />
                <Route path="configuracoes" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <ConfiguracoesSite />
                  </AdminProtectedRoute>
                } />
                <Route path="verificacao-usuarios" element={
                  <AdminProtectedRoute>
                    <VerificacaoUsuariosAdmin />
                  </AdminProtectedRoute>
                } />
                <Route path="diagnostico-visibilidade" element={
                  <AdminProtectedRoute>
                    <DiagnosticoVisibilidade />
                  </AdminProtectedRoute>
                } />
                <Route path="seo" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <PainelSEO />
                  </AdminProtectedRoute>
                } />
                <Route path="integracoes" element={
                  <AdminProtectedRoute adminMasterOnly>
                    <Integracoes />
                  </AdminProtectedRoute>
                } />
                <Route path="seguranca" element={<Seguranca2FA />} />
                <Route path="painel-social" element={<PainelSocial />} />
                <Route path="organizacoes-sociais" element={<OrganizacoesSociaisAdmin />} />
                <Route path="vitrines" element={<VitrinesAdmin />} />
                <Route path="painel-negocios" element={<PainelNegocios />} />
                <Route path="painel-empreendedores" element={<PainelEmpreendedores />} />

                {/* DPO Panel — LGPD compliance */}
                <Route path="dpo" element={<DpoPainel />} />
                <Route path="dpo/nomeacao" element={<DpoNomeacao />} />
                <Route path="dpo/titulares" element={<DpoTitulares />} />
                <Route path="dpo/incidentes" element={<DpoIncidentes />} />
                <Route path="dpo/mapeamento" element={<DpoMapeamento />} />
                <Route path="dpo/subprocessadores" element={<DpoSubprocessadores />} />
                <Route path="dpo/treinamentos" element={<DpoTreinamentos />} />
                <Route path="dpo/templates" element={<DpoTemplates />} />
                <Route path="dpo/relatorios" element={<DpoRelatorios />} />
                <Route path="dpo/export" element={<DpoExport />} />

                {/* Sistema de Recomendações */}
                <Route path="recomendacoes" element={<Recomendacoes />} />

                {/* Campanhas */}
                <Route path="campanhas" element={<CampanhasLista />} />
                <Route path="campanhas/nova" element={<CampanhasForm />} />
                <Route path="campanhas/:id/editar" element={<CampanhasForm />} />
                <Route path="campanhas/:id/relatorio" element={<CampanhaRelatorio />} />
                <Route path="coletivos" element={<ColetivosLista />} />
                <Route path="coletivos/novo" element={<ColetivoForm />} />
                <Route path="coletivos/:id/editar" element={<ColetivoForm />} />
                <Route path="coletivos/:id/inscritos" element={<ColetivoInscritos />} />
                <Route path="faq" element={<FAQAdmin />} />
                <Route path="hero-banners" element={<HeroBanners />} />
                <Route path="ajuda" element={<AjudaInterna />} />
              </Route>

              {/* Public LGPD request form (no auth required) */}
              <Route path="/privacidade/solicitacao" element={<MainLayout><PrivacidadeSolicitacao /></MainLayout>} />

              {/* Municipality-prefixed routes (e.g., /ponte-nova/explorar) */}
              <Route path="/:municipioSlug/*" element={<TenantRoutes />} />

              {/* Catch-all 404 — must be last */}
              <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
            </Routes>
          </FavoritesProvider>
        </LocationProvider>
      </AuthProvider>
      </DomainMunicipioProvider>
      <PWAInstallBanner />
      <TrackingScripts />
      <SiteFavicon />
      <PWASplashLinks />
      <CookieConsent />
      <Toaster position="top-right" richColors closeButton />
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
